import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["onConfirm", "title", "type"];
import { useCallback, useMemo, useState } from 'react';
import unescapedText from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UISelect from 'UIComponents/input/UISelect';
import UITextArea from 'UIComponents/input/UITextArea';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import BaseReviewModal from './BaseReviewModal';
import { Reportables, ReportReviewReasons, DeprecatedReportReviewReasons, ReportReplyReasons } from '../../constants/ReportReasons';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ReportReviewModal(_ref) {
  let {
      onConfirm,
      title = /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplace-ui-reviews.modal.report.title"
      }),
      type = Reportables.REVIEW
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [reason, setReason] = useState(null);
  const [comment, setComment] = useState('');
  const reasonOptions = useMemo(() => {
    const reportReviewsEntries = Object.entries(ReportReviewReasons);
    const filteredReportReviewReasons = reportReviewsEntries.reduce((acc, [key, value]) => {
      if (DeprecatedReportReviewReasons.includes(key)) return acc;
      acc[key] = value;
      return acc;
    }, {});
    const reasons = type === Reportables.REVIEW ? filteredReportReviewReasons : ReportReplyReasons;
    return Object.values(reasons).map(opt => {
      return {
        text: unescapedText(`marketplace-ui-reviews.reportReasons.${type}.${opt}.description`),
        value: opt
      };
    });
  }, [type]);
  const getReportErrorKey = err => {
    const errorMessage = err.responseJSON && err.responseJSON.message || err.networkError && err.networkError.responseJSON && err.networkError.responseJSON.message;
    const errorCategory = err.responseJSON && err.responseJSON.category || err.networkError && err.networkError.responseJSON && err.networkError.responseJSON.category;
    if (errorMessage && errorMessage.includes('already reported')) {
      return 'duplicate_error';
    } else if (errorCategory === 'RATE_LIMITS') {
      return 'report_limit_error';
    }
    return 'error';
  };
  const handleConfirm = useCallback(() => {
    const confirmPromise = onConfirm({
      reason,
      comment
    });
    if (confirmPromise instanceof Promise) {
      confirmPromise.then(() => {
        FloatingAlertStore.addAlert({
          message: unescapedText('marketplace-ui-reviews.modal.report.success.message'),
          titleText: unescapedText('marketplace-ui-reviews.modal.report.success.titleText'),
          type: 'success'
        });
      }).catch(err => {
        const errorKey = getReportErrorKey(err);
        FloatingAlertStore.addAlert({
          message: unescapedText(`marketplace-ui-reviews.modal.report.${errorKey}.message`),
          titleText: unescapedText(`marketplace-ui-reviews.modal.report.${errorKey}.titleText`),
          type: 'danger'
        });
      });
    }
  }, [comment, onConfirm, reason]);
  const form = useMemo(() => {
    return /*#__PURE__*/_jsxs(UIForm, {
      children: [/*#__PURE__*/_jsx(UIFormControl, {
        required: true,
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.modal.report.reason"
        }),
        children: /*#__PURE__*/_jsx(UISelect, {
          "data-test-id": "report-reason-select",
          onChange: ({
            target: {
              value
            }
          }) => setReason(value),
          options: reasonOptions,
          value: reason,
          placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-ui-reviews.modal.report.reasonPlaceholder"
          })
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        required: true,
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-ui-reviews.modal.report.comment"
        }),
        children: /*#__PURE__*/_jsx(UITextArea, {
          "data-test-id": "report-comment-input",
          placeholder: unescapedText('marketplace-ui-reviews.modal.report.commentPlaceholder'),
          value: comment,
          onChange: ({
            target: {
              value
            }
          }) => setComment(value),
          rows: 6
        })
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        className: "m-top-2",
        message: "marketplace-ui-reviews.modal.report.confirmation"
      })]
    });
  }, [comment, reason, reasonOptions]);
  return /*#__PURE__*/_jsx(BaseReviewModal, Object.assign({}, rest, {
    title: title,
    form: form,
    confirmDisabled: !reason || !comment,
    onConfirm: handleConfirm,
    tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-ui-reviews.modal.report.tooltip"
    })
  }));
}